<template>
  <m-dropdown
    v-model:value="show"
    :block="props.block"
    class="goal-create-dropdown"
    :trigger="props.trigger"
    :title="$t('goalCreateDropdown.title')"
    :match-trigger-width="props.matchTriggerWidth"
    :placement="props.placement"
    @hide="emit('hide')"
  >
    <div
      :style="{ width: '100%' }"
      @click.stop="handleClick"
    >
      <slot :disabled="allowedSubItemGoalTypes.length === 0" />
    </div>
    <template #overlay>
      <m-card
        list
      >
        <m-card-item
          v-for="(item, i) in allowedSubItemGoalTypes"
          :key="i"
          @click="handleItemClick(item)"
        >
          {{ getLabel(item.label) }}
        </m-card-item>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import useGoalTypeProperty from '@/composables/customize-page/goal-type-property';
import useGoals from '@/composables/goal/goals';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import { computed, ref } from 'vue';
import { depth, idFromIndex } from '@/lib/sort-operation';
import {
  getAllowedParentItemGoalTypes,
  getAllowedSubItemGoalTypes,
  goalTypeOption,
} from '@/composables/goal/allowed-sub-items';
import { intersectionBy } from 'lodash-es';
import { textByLang } from 'shared/lib/language';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  matchTriggerWidth: {
    type: Boolean,
    default: false,
  },
  goal: {
    type: Object,
    default: () => null,
  },
  index: {
    type: String,
    default: '',
  },
  block: {
    type: Boolean,
    default: false,
  },
  placement: {
    type: String,
    default: 'bottomCenter',
  },
  insertPosition: {
    type: String,
    default: 'bottom',
  },
  trigger: {
    type: String,
    default: 'click',
  },
});

const emit = defineEmits(['hide', 'create-beneath']);

const { t } = useI18n();
const { userLang } = useLoggedInUser();
const { goalTypeProperty } = useGoalTypeProperty();
const goalSelector = useGoals();

const show = ref(false);

const parent = computed(() => {
  const d = depth(props.index);
  if (d === 0) {
    return null;
  }
  const parentId = idFromIndex(props.index, d - 1);
  return goalSelector.selectSingle(parentId);
});

const goal = computed(() => (props.goal ? goalSelector.selectSingle(props.goal.uid) : null));

const allowedSubItemGoalTypes = computed(() => {
  if (goal.value === null) {
    return getAllowedSubItemGoalTypes(null, goalTypeProperty.value);
  }

  if (props.insertPosition === 'top') {
    if (parent.value === null) {
      return getAllowedParentItemGoalTypes(goalTypeOption(goal.value, goalTypeProperty.value), goalTypeProperty.value);
    }
    const allowedChildren = getAllowedSubItemGoalTypes(goalTypeOption(parent.value, goalTypeProperty.value), goalTypeProperty.value);
    const allowedParents = getAllowedParentItemGoalTypes(goalTypeOption(goal.value, goalTypeProperty.value), goalTypeProperty.value);
    return intersectionBy(allowedChildren, allowedParents, 'uid');
  }
  if (['right', 'left'].includes(props.insertPosition)) {
    // if parent is null, we can use all types
    if (parent.value === null) {
      return goalTypeProperty.value.options.map((o) => ({ ...o, property: goalTypeProperty.value }));
    }
    return getAllowedSubItemGoalTypes(goalTypeOption(parent.value, goalTypeProperty.value), goalTypeProperty.value);
  }
  return getAllowedSubItemGoalTypes(goalTypeOption(goal.value, goalTypeProperty.value), goalTypeProperty.value);
});

const getLabel = (label) => {
  const res = textByLang(label, userLang.value);
  if (res === '') {
    return t('general.untitled');
  }
  return res;
};

const handleClick = () => {
  if (allowedSubItemGoalTypes.value.length === 0) {
    return;
  }

  if (allowedSubItemGoalTypes.value.length === 1) {
    handleItemClick(allowedSubItemGoalTypes.value[0]);
    return;
  }

  show.value = true;
};

const handleItemClick = (item) => {
  emit('create-beneath', { parent: goal.value, option: item });
  show.value = false;
};
</script>

<style scoped lang="scss" type="text/scss">
</style>
